import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ScheduleConsultationModal from '../../components/ui/ScheduleConsultationModal';

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

  // Course data - same as in the main page
  const courses = [
    {
      id: 1,
      title: 'Chinese Language 101',
      description: 'Perfect for complete beginners. Learn basic Mandarin, essential phrases, and cultural basics.',
      category: 'beginner',
      icon: 'BookOpen',
      level: 'Beginner',
      instructor: 'Li Wei',
      instructorBio: 'Li Wei is a certified TCFL (Teaching Chinese as a Foreign Language) instructor with over 8 years of experience teaching international students. She specializes in making Chinese accessible to beginners and has helped thousands of students take their first steps in Mandarin.',
      instructorExperience: '8+ years teaching experience',
      instructorStudents: 2500,
      instructorRating: 5,
      duration: '8 weeks • 24 hours',
      durationWeeks: 8,
      hoursPerWeek: 3,
      students: 2547,
      rating: 5,
      reviews: 489,
      price: 99,
      demoVideoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://images.unsplash.com/photo-1513338261602-ecdc8035d8cf?w=800',
      whatYouWillLearn: [
        'Master Chinese pinyin and tone system',
        'Learn 300+ essential vocabulary words',
        'Understand basic grammar structures',
        'Practice introductions and common greetings',
        'Explore Chinese culture and traditions',
        'Build confidence in basic conversations',
      ],
      prerequisites: ['No prior knowledge required', 'Internet connection', 'Microphone for practice sessions'],
      schedule: [
        { week: 'Week 1-2', topic: 'Pinyin, Tones & Basic Greetings' },
        { week: 'Week 3-4', topic: 'Family & Numbers (1-100)' },
        { week: 'Week 5-6', topic: 'Daily Activities & Food' },
        { week: 'Week 7-8', topic: 'Shopping & Cultural Practices' },
      ],
      courseStructure: [
        { type: 'Live Classes', description: '2 hours per week, interactive sessions with instructor', icon: 'Video' },
        { type: 'Recorded Lessons', description: 'Access to all class recordings for review', icon: 'Repeat' },
        { type: 'Practice Exercises', description: 'Daily homework and practice materials', icon: 'CheckCircle' },
        { type: 'Group Discussions', description: 'Practice conversations with other students', icon: 'Users' },
        { type: 'Resource Materials', description: 'Comprehensive study guides and flashcards', icon: 'Book' },
        { type: 'Certificate', description: 'Completion certificate upon finishing the course', icon: 'Award' },
      ],
      certificationInfo: 'Earn an official completion certificate that recognizes your achievement in Chinese Language fundamentals.',
      accessDuration: 'Lifetime access',
      refundPolicy: '30-day money-back guarantee',
      supportAvailable: true,
      supportChannels: ['Email', 'Live Chat', 'Community Forum'],
    },
    {
      id: 2,
      title: 'Intermediate Conversational Chinese',
      description: 'Build confidence in everyday conversations, expand vocabulary, and improve pronunciation.',
      category: 'intermediate',
      icon: 'MessageCircle',
      level: 'Intermediate',
      instructor: 'Wang Chen',
      instructorBio: 'Wang Chen is a native Mandarin speaker and professional Chinese language instructor focused on practical communication and pronunciation mastery. With 10+ years of experience, he has trained professionals in multinational companies.',
      instructorExperience: '10+ years experience',
      instructorStudents: 4200,
      instructorRating: 5,
      duration: '10 weeks • 30 hours',
      durationWeeks: 10,
      hoursPerWeek: 3,
      students: 1823,
      rating: 5,
      reviews: 324,
      price: 129,
      demoVideoUrl: 'https://www.youtube.com/embed/jNQXAC9IVRw',
      imageUrl: 'https://images.unsplash.com/photo-1516321318423-f06f70a504f9?w=800',
      whatYouWillLearn: [
        'Hold natural conversations with native speakers',
        'Expand vocabulary to 1000+ words',
        'Master intermediate grammar patterns',
        'Improve listening comprehension',
        'Perfect your pronunciation and tone accuracy',
        'Discuss current events and personal experiences',
      ],
      prerequisites: ['Basic knowledge of Chinese (HSK 1-2 level)', 'Completion of Chinese 101 or equivalent'],
      schedule: [
        { week: 'Week 1-3', topic: 'Review & Intermediate Grammar' },
        { week: 'Week 4-6', topic: 'Practical Conversations: Travel & Dining' },
        { week: 'Week 7-8', topic: 'Workplace & Professional Settings' },
        { week: 'Week 9-10', topic: 'Advanced Conversations & Free Discussion' },
      ],
      courseStructure: [
        { type: 'Live Classes', description: '2.5 hours per week, conversation-focused sessions', icon: 'Video' },
        { type: 'Recorded Lessons', description: 'Access to all class recordings for review', icon: 'Repeat' },
        { type: 'Practice Exercises', description: 'Speaking practice with AI and peer feedback', icon: 'CheckCircle' },
        { type: 'Group Discussions', description: 'Real-time conversation practice with classmates', icon: 'Users' },
        { type: 'Resource Materials', description: 'Vocabulary lists, dialogues, and cultural notes', icon: 'Book' },
        { type: 'Certificate', description: 'Proficiency certificate upon completion', icon: 'Award' },
      ],
      certificationInfo: 'Earn an intermediate level proficiency certificate recognized by language institutes.',
      accessDuration: 'Lifetime access',
      refundPolicy: '30-day money-back guarantee',
      supportAvailable: true,
      supportChannels: ['Email', 'Live Chat', 'Community Forum', 'One-on-One Sessions'],
    },
    {
      id: 3,
      title: 'Intermediate Business Chinese 1',
      description: 'The MOOC of Intermediate Business Chinese is designed for the Chinese language learners who have reached HSK level 3. The MOOC presents lively, authentic, and up-to-date dialogues for daily conversations and business communications. The contents integrate trendy technologies with real-life situations that can be applied to everyday lives, language classrooms, and working places. The course also covers all the key grammar points and patterns in HSK level 3-5. Students are expected to reach HSK level 4-5 when they complete the course and be able to effectively communicate in daily lives and professional settings from intermediate-mid to advanced-low levels.',
      category: 'mooc',
      icon: 'Briefcase',
      level: 'Intermediate',
      instructor: 'Shanghai Jiaotong University',
      instructorBio: 'Shanghai Jiaotong University is one of China\'s leading universities with an exceptional program in Chinese language education for international learners. Their faculty specialize in business Chinese and professional communication.',
      instructorExperience: 'Shanghai Jiaotong University Faculty',
      instructorStudents: 8500,
      instructorRating: 5,
      duration: '5 weeks • 22 hours',
      durationWeeks: 5,
      hoursPerWeek: 4.4,
      students: 8500,
      rating: 5,
      reviews: 1234,
      price: 0,
      courseraUrl: 'https://www.coursera.org/learn/intermediate-business-chinese-1',
      demoVideoUrl: 'https://www.coursera.org/learn/intermediate-business-chinese-1',
      imageUrl: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800',
      whatYouWillLearn: [
        'Master intermediate business communication in Chinese',
        'Learn professional vocabulary and terminology',
        'Handle business emails and formal correspondence',
        'Conduct effective meetings and negotiations',
        'Present professionally in business settings',
        'Understand corporate Chinese business culture',
      ],
      prerequisites: ['Intermediate Chinese language knowledge (HSK 2-3 level)', 'Basic business communication skills'],
      schedule: [
        { week: 'Module 1', topic: 'I Want to Get a Transportation Card (4 hours)' },
        { week: 'Module 2', topic: 'Do You Need to Go to the Airport? (4 hours)' },
        { week: 'Module 3', topic: 'Should I Wait Here for the Bus? (5 hours)' },
        { week: 'Module 4', topic: 'How Did You Come Here? (5 hours)' },
        { week: 'Module 5', topic: 'I Booked a Single Room (4 hours)' },
      ],
      courseStructure: [
        { type: 'Video Lectures', description: '3 videos per module covering business and daily scenarios', icon: 'Video' },
        { type: 'Readings', description: '8-9 readings per module with authentic business dialogues', icon: 'Book' },
        { type: 'Assignments', description: '5-7 assignments per module with practical business communication exercises', icon: 'CheckCircle' },
        { type: 'Peer Reviews', description: '1 peer review per module for collaborative learning', icon: 'Users' },
        { type: 'Discussion Forums', description: '1-2 discussion prompts per module to apply language skills', icon: 'MessageSquare' },
        { type: 'Certificate', description: 'Verified Coursera certificate of completion', icon: 'Award' },
      ],
      certificationInfo: 'Upon completion, earn a Coursera Certificate of Completion recognized by employers. This course develops professional business Chinese communication competency.',
      accessDuration: '6 months access (with option to extend)',
      refundPolicy: '7-day free trial, full refund available',
      supportAvailable: true,
      supportChannels: ['Discussion Forums', 'Community Support', 'Course Messages'],
    },
    {
      id: 4,
      title: 'HSK Examination Prep',
      description: 'Comprehensive preparation for HSK certification tests with practice exams and expert tips.',
      category: 'certification',
      icon: 'Award',
      level: 'All Levels',
      instructor: 'Liu Ming',
      instructorBio: 'Liu Ming is an official HSK examiner with 9+ years of examination preparation experience. He has helped over 3,000 students achieve their target HSK levels with a 92% success rate.',
      instructorExperience: 'HSK Examiner, 9+ years',
      instructorStudents: 3500,
      instructorRating: 5,
      duration: '6 weeks • 20 hours',
      durationWeeks: 6,
      hoursPerWeek: 3.3,
      students: 3124,
      rating: 5,
      reviews: 612,
      price: 89,
      demoVideoUrl: 'https://www.youtube.com/embed/kDKu_yJ5LCA',
      imageUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800',
      whatYouWillLearn: [
        'Master all HSK levels (1-6) exam formats',
        'Intensive vocabulary training (2500+ words)',
        'Listening, reading, and writing strategies',
        'Time management for exam sections',
        'Common test patterns and question types',
        'Build confidence for test day',
      ],
      prerequisites: ['Basic to advanced Chinese knowledge depending on target level'],
      schedule: [
        { week: 'Week 1-2', topic: 'HSK Format Overview & Strategies' },
        { week: 'Week 3-4', topic: 'Full-length Practice Tests' },
        { week: 'Week 5-6', topic: 'Final Review & Test-taking Tips' },
      ],
      courseStructure: [
        { type: 'Live Classes', description: '2-3 hours per week of focused prep', icon: 'Video' },
        { type: 'Recorded Lessons', description: 'Strategy videos and test walkthroughs', icon: 'Repeat' },
        { type: 'Practice Exercises', description: '20+ full-length practice exams', icon: 'CheckCircle' },
        { type: 'Group Discussions', description: 'Q&A sessions with examiner', icon: 'Users' },
        { type: 'Resource Materials', description: 'Official HSK practice materials', icon: 'Book' },
        { type: 'Certificate', description: 'Official HSK Certificate upon passing', icon: 'Award' },
      ],
      certificationInfo: 'Prepare for official HSK certification recognized internationally by universities and employers.',
      accessDuration: 'Access until exam date',
      refundPolicy: '14-day money-back guarantee',
      supportAvailable: true,
      supportChannels: ['Email', 'Live Chat', '1-on-1 Tutoring', 'Mock Exam Feedback'],
    },
    {
      id: 5,
      title: 'Chinese Culture & Language Integration',
      description: 'Learn Chinese through cultural immersion: history, traditions, festivals, and literature.',
      category: 'culture',
      icon: 'Palette',
      level: 'Intermediate',
      instructor: 'Professor Yang Su',
      instructorBio: 'Professor Yang Su is a university professor specializing in Chinese cultural studies and linguistics. He integrates Chinese history, literature, and traditions into comprehensive language learning programs.',
      instructorExperience: 'Professor, 15+ years',
      instructorStudents: 2100,
      instructorRating: 5,
      duration: '8 weeks • 28 hours',
      durationWeeks: 8,
      hoursPerWeek: 3.5,
      students: 987,
      rating: 5,
      reviews: 156,
      price: 119,
      demoVideoUrl: 'https://www.youtube.com/embed/n0RH3gHALzY',
      imageUrl: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800',
      whatYouWillLearn: [
        'Understand Chinese history and philosophy',
        'Learn through classical Chinese poetry and literature',
        'Explore traditional festivals and celebrations',
        'Master cultural context for language use',
        'Appreciate Chinese art, music, and cuisine',
        'Connect language to living culture',
      ],
      prerequisites: ['Basic Chinese knowledge', 'Interest in Chinese culture'],
      schedule: [
        { week: 'Week 1-2', topic: 'Ancient Chinese History & Language' },
        { week: 'Week 3-4', topic: 'Poetry, Literature & Arts' },
        { week: 'Week 5-6', topic: 'Festivals, Traditions & Celebrations' },
        { week: 'Week 7-8', topic: 'Modern China & Contemporary Culture' },
      ],
      courseStructure: [
        { type: 'Live Classes', description: '3+ hours per week of cultural exploration', icon: 'Video' },
        { type: 'Recorded Lessons', description: 'Documentary and cultural content', icon: 'Repeat' },
        { type: 'Practice Exercises', description: 'Cultural analysis and language application', icon: 'CheckCircle' },
        { type: 'Group Discussions', description: 'Cultural discussion forums', icon: 'Users' },
        { type: 'Resource Materials', description: 'Poetry, literature, and cultural guides', icon: 'Book' },
        { type: 'Certificate', description: 'Cultural Proficiency Certificate', icon: 'Award' },
      ],
      certificationInfo: 'Receive certification in Chinese Cultural Proficiency and Language Integration.',
      accessDuration: 'Lifetime access',
      refundPolicy: '30-day money-back guarantee',
      supportAvailable: true,
      supportChannels: ['Email', 'Community Forum', 'Cultural Discussions'],
    },
    {
      id: 6,
      title: 'Advanced Mandarin Mastery',
      description: 'Achieve fluency with advanced grammar, idioms, literature analysis, and native-level proficiency.',
      category: 'advanced',
      icon: 'Brain',
      level: 'Advanced',
      instructor: 'Prof. Ma Wei',
      instructorBio: 'Prof. Ma Wei is an accomplished professor with 18+ years of experience in advanced Chinese linguistics. He specializes in bringing non-native speakers to native-level proficiency and has published research on Chinese language pedagogy.',
      instructorExperience: 'Prof., 18+ years',
      instructorStudents: 2800,
      instructorRating: 5,
      duration: '14 weeks • 45 hours',
      durationWeeks: 14,
      hoursPerWeek: 3.2,
      students: 654,
      rating: 5,
      reviews: 189,
      price: 179,
      demoVideoUrl: 'https://www.youtube.com/embed/eJnQBXmHsSU',
      imageUrl: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800',
      whatYouWillLearn: [
        'Advanced grammar and complex sentence structures',
        'Master idioms (chengyu) and proverbs',
        'Analyze Chinese literature and news articles',
        'Develop native-like accent and fluency',
        'Specialized vocabulary for various fields',
        'Write formally and persuasively in Chinese',
      ],
      prerequisites: ['Advanced intermediate to advanced level (HSK 5+)', 'Significant prior Chinese study'],
      schedule: [
        { week: 'Week 1-4', topic: 'Advanced Grammar & Complex Sentences' },
        { week: 'Week 5-8', topic: 'Idioms, Literature & Media Analysis' },
        { week: 'Week 9-12', topic: 'Specialized Topics & Academic Writing' },
        { week: 'Week 13-14', topic: 'Fluency Refinement & Capstone Project' },
      ],
      courseStructure: [
        { type: 'Live Classes', description: '3+ hours per week of advanced instruction', icon: 'Video' },
        { type: 'Recorded Lessons', description: 'Advanced content and literary analyses', icon: 'Repeat' },
        { type: 'Practice Exercises', description: 'Writing assignments and debates', icon: 'CheckCircle' },
        { type: 'Group Discussions', description: 'Advanced seminars and discussions', icon: 'Users' },
        { type: 'Resource Materials', description: 'Academic texts, articles, and literature', icon: 'Book' },
        { type: 'Certificate', description: 'Advanced Proficiency Certificate', icon: 'Award' },
      ],
      certificationInfo: 'Earn advanced proficiency certification, opening doors to advanced professional opportunities.',
      accessDuration: 'Lifetime access + premium materials',
      refundPolicy: '30-day money-back guarantee',
      supportAvailable: true,
      supportChannels: ['Email', 'Live Chat', 'Private Coaching', 'One-on-One Mentoring'],
    },
    {
      id: 7,
      title: 'Chinese for HSK1',
      description: 'Nǐ hǎo! 你好！Welcome to Chinese for HSK Level 1 - a beginner course by Peking University. HSK (Hànyǔ Shuǐpíng Kǎoshì) is the most important Chinese proficiency test assessing non-native speakers\' abilities in daily, academic and professional life. This course covers 30 video lectures, 150+ commonly used words, 50 key grammar points, and prepares you for HSK1 certification. You will learn the four language skills and be able to understand and use simple Chinese phrases, meet basic communication needs, and further your Chinese studies.',
      category: 'mooc',
      icon: 'Award',
      level: 'Beginner',
      instructor: 'Yu Bin, Peking University',
      instructorBio: 'Yu Bin is an experienced instructor from Peking University, one of China\'s most prestigious universities. PKU faculty are experts in teaching Mandarin Chinese to international learners. This course features dialogue role plays by PKU students and supplementary materials about China.',
      instructorExperience: 'Peking University Faculty',
      instructorStudents: 45000,
      instructorRating: 5,
      duration: '7 weeks • 15 hours',
      durationWeeks: 7,
      hoursPerWeek: 2.14,
      students: 45000,
      rating: 5,
      reviews: 8234,
      price: 0,
      courseraUrl: 'https://www.coursera.org/learn/hsk-1',
      demoVideoUrl: 'https://www.coursera.org/learn/hsk-1/lecture/rJcyx/welcome-to-chinese-for-hsk-level-1',
      imageUrl: 'https://images.unsplash.com/photo-1513338261602-ecdc8035d8cf?w=800',
      whatYouWillLearn: [
        '30 video lectures with dialogue role plays by PKU students',
        'Learn 150+ commonly used words for everyday use',
        'Master 50 key grammar points essential for HSK1',
        'Study new words and texts via audio materials',
        'Practice with quizzes for each lesson and HSK test papers',
        'Optional: Videos for 70 basic Chinese characters',
        'Optional: Supplementary materials about China',
        'Develop listening, reading, speaking, and writing skills',
      ],
      prerequisites: ['No prior knowledge required', 'Internet connection', 'Computer with audio and video capabilities', 'Microphone for practice (optional)'],
      schedule: [
        { week: 'Module 1', topic: 'Introduction to Mandarin & Pinyin System' },
        { week: 'Module 2', topic: 'Basic Greetings & Common Phrases' },
        { week: 'Module 3', topic: 'Numbers & Time Expressions' },
        { week: 'Module 4', topic: 'Family & Personal Information' },
        { week: 'Module 5', topic: 'Daily Activities & Food' },
        { week: 'Module 6', topic: 'Shopping & Asking for Help' },
        { week: 'Module 7', topic: 'HSK1 Practice Exam & Certification Prep' },
      ],
      courseStructure: [
        { type: 'Video Lectures', description: '30 video lectures with dialogue role plays by PKU students', icon: 'Video' },
        { type: 'Vocabulary', description: '150+ commonly used words with audio materials', icon: 'BookOpen' },
        { type: 'Grammar Points', description: '50 key grammar points essential for HSK1', icon: 'Edit' },
        { type: 'Quizzes & Exams', description: 'Quizzes for each lesson and official HSK test papers', icon: 'Award' },
        { type: 'Character Videos', description: 'Optional videos for 70 basic Chinese characters', icon: 'Type' },
        { type: 'Cultural Materials', description: 'Optional supplementary materials about China', icon: 'Globe' },
        { type: 'Certificate', description: 'Coursera certificate of completion upon finishing the course', icon: 'Award' },
      ],
      certificationInfo: 'Upon completion, earn a Coursera Certificate of Completion. This course aligns with HSK Level 1 (equivalent to A1 of Common European Framework of Reference for Languages). At the end, you will understand and use simple Chinese phrases, meet basic communication needs, and be prepared for further Chinese language studies and the official HSK1 certification exam.',
      accessDuration: '6 months access (with option to extend)',
      refundPolicy: '7-day free trial, full refund available',
      supportAvailable: true,
      supportChannels: ['Discussion Forums', 'Community Support', 'Course Messages'],
    },
    {
      id: 8,
      title: 'Chinese for HSK2',
      description: 'Welcome to HSK Level 2 course by Peking University! This course is the second step towards HSK Level 6. At the end of 6 weeks, you will be able to express your views towards simple and common daily life topics. The course features 24 lecture videos, 44 complementary short videos, 150+ new vocabularies, 62 new grammar points, and comprehensive practice materials. No need to complete HSK1 - just basic Chinese language competency is required.',
      category: 'mooc',
      icon: 'Award',
      level: 'Elementary',
      instructor: 'Peking University',
      instructorBio: 'Peking University is one of China\'s most prestigious universities, renowned for its Chinese language education programs. Their faculty are experts in teaching Mandarin Chinese to international learners with engaging and comprehensive curricula.',
      instructorExperience: 'Peking University Faculty',
      instructorStudents: 28000,
      instructorRating: 5,
      duration: '6 weeks • 34 hours',
      durationWeeks: 6,
      hoursPerWeek: 5.67,
      students: 28000,
      rating: 5,
      reviews: 4156,
      price: 0,
      courseraUrl: 'https://www.coursera.org/learn/hsk-2',
      demoVideoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://images.unsplash.com/photo-1513338261602-ecdc8035d8cf?w=800',
      whatYouWillLearn: [
        '24 lecture videos with in-class exercises for comprehensive learning',
        '44 complementary short videos supporting the learning materials',
        'Master 150+ new vocabulary words for HSK2 level',
        'Learn 62 new grammar points essential for HSK2',
        'Study and practice over 100 basic Chinese characters with downloadable charts',
        'Participate in weekly online quizzes to reinforce learning',
        'Take final simulation exam questions to prepare for HSK2 certification',
        'Express your views on simple and common daily life topics',
        'Build foundation for progression towards HSK Level 6',
      ],
      prerequisites: ['Basic Chinese language competency', 'No need to complete HSK1 if you have foundational knowledge', 'Internet connection and audio/video capabilities'],
      schedule: [
        { week: 'Module 1', topic: 'Talk about your daily life 1 (3 hours)' },
        { week: 'Module 2', topic: 'Talk about your daily life 2 (3 hours)' },
        { week: 'Module 3', topic: 'Help and ask for help (3 hours)' },
        { week: 'Module 4', topic: 'Invitation and Reply (3 hours)' },
        { week: 'Module 5', topic: 'Ask for opinions, comments and suggestion (3 hours)' },
        { week: 'Module 6', topic: 'Discussion personal experience and feelings (3 hours)' },
        { week: 'Module 7', topic: 'Final Test (50 minutes)' },
      ],
      courseStructure: [
        { type: 'Lecture Videos', description: '24 lecture videos with in-class exercises', icon: 'Video' },
        { type: 'Complementary Videos', description: '44 short videos supporting learning materials', icon: 'PlayCircle' },
        { type: 'Vocabulary', description: '150+ new vocabulary words with pictures and examples', icon: 'BookOpen' },
        { type: 'Grammar Points', description: '62 new grammar points with relevant examples', icon: 'Edit' },
        { type: 'Character Practice', description: 'Downloadable practice charts for 100+ basic Chinese characters', icon: 'Type' },
        { type: 'Quizzes', description: 'Weekly online quizzes to reinforce learning', icon: 'CheckCircle' },
        { type: 'Final Exam', description: 'Final simulation exam questions for HSK2 certification prep', icon: 'Award' },
        { type: 'Certificate', description: 'Verified Coursera certificate of completion', icon: 'Award' },
      ],
      certificationInfo: 'Upon completion, earn a Coursera Certificate of Completion. This course covers the entire content stated in the HSK Level Two Outline and prepares you for the official HSK2 certification exam. You will be able to express your views on common daily life topics and continue progressing towards higher HSK levels.',
      accessDuration: '6 months access (with option to extend)',
      refundPolicy: '7-day free trial, full refund available',
      supportAvailable: true,
      supportChannels: ['Discussion Forums', 'Community Support', 'Course Messages'],
    },
    {
      id: 9,
      title: 'Chinese for HSK 3 Part 1',
      description: 'Official Coursera course by Peking University. Advance to upper-intermediate proficiency with complex grammar and conversational skills.',
      category: 'mooc',
      icon: 'Award',
      level: 'Intermediate',
      instructor: 'Peking University',
      instructorBio: 'Peking University is one of China\'s most prestigious universities, renowned for its Chinese language education programs. Their faculty are experts in teaching Mandarin Chinese to international learners with engaging and comprehensive curricula.',
      instructorExperience: 'Peking University Faculty',
      instructorStudents: 15000,
      instructorRating: 5,
      duration: '4 weeks • 18 hours',
      durationWeeks: 4,
      hoursPerWeek: 4.5,
      students: 15000,
      rating: 5,
      reviews: 2234,
      price: 0,
      courseraUrl: 'https://www.coursera.org/learn/hsk-3',
      demoVideoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://images.unsplash.com/photo-1513338261602-ecdc8035d8cf?w=800',
      whatYouWillLearn: [
        'Master upper-intermediate Mandarin Chinese pronunciation',
        'Learn 600+ essential HSK3 vocabulary words',
        'Understand complex grammar structures and sentence patterns',
        'Develop fluent conversational abilities in various contexts',
        'Master Chinese narratives and storytelling',
        'Prepare for official HSK3 certification exam',
      ],
      prerequisites: ['Completion of Chinese for HSK2 or equivalent knowledge', 'Strong understanding of intermediate Mandarin Chinese'],
      schedule: [
        { week: 'Week 1', topic: 'Complex Grammar & Advanced Pronunciation' },
        { week: 'Week 2', topic: 'HSK3 Vocabulary Expansion & Expressions' },
        { week: 'Week 3', topic: 'Conversational Fluency & Narratives' },
        { week: 'Week 4', topic: 'HSK3 Practice Exam & Certification Prep' },
      ],
      courseStructure: [
        { type: 'Video Lectures', description: 'High-quality instructional videos from Peking University', icon: 'Video' },
        { type: 'Practice Exercises', description: 'Interactive drills and vocabulary practice', icon: 'CheckCircle' },
        { type: 'Quizzes & Exams', description: 'Graded assessments and practice HSK3 exams', icon: 'Award' },
        { type: 'Discussion Forums', description: 'Peer support and community learning', icon: 'Users' },
        { type: 'Resource Materials', description: 'Downloadable study guides and vocabulary lists', icon: 'Book' },
        { type: 'Certificate', description: 'Verified Coursera certificate of completion', icon: 'Award' },
      ],
      certificationInfo: 'Upon completion, earn a Coursera Certificate of Completion recognized by employers and educational institutions worldwide. This course prepares you for the official HSK3 certification exam.',
      accessDuration: '6 months access (with option to extend)',
      refundPolicy: '7-day free trial, full refund available',
      supportAvailable: true,
      supportChannels: ['Discussion Forums', 'Community Support', 'Course Messages'],
    },
    {
      id: 10,
      title: 'Chinese for HSK 3 Part 2',
      description: 'Official Coursera course by Peking University. Complete HSK3 proficiency with advanced conversational and writing skills.',
      category: 'mooc',
      icon: 'Award',
      level: 'Intermediate',
      instructor: 'Peking University',
      instructorBio: 'Peking University is one of China\'s most prestigious universities, renowned for its Chinese language education programs. Their faculty are experts in teaching Mandarin Chinese to international learners with engaging and comprehensive curricula.',
      instructorExperience: 'Peking University Faculty',
      instructorStudents: 12000,
      instructorRating: 5,
      duration: '4 weeks • 18 hours',
      durationWeeks: 4,
      hoursPerWeek: 4.5,
      students: 12000,
      rating: 5,
      reviews: 1876,
      price: 0,
      courseraUrl: 'https://www.coursera.org/learn/hsk-3-part2',
      demoVideoUrl: 'https://www.coursera.org/learn/hsk-3-part2',
      imageUrl: 'https://images.unsplash.com/photo-1513338261602-ecdc8035d8cf?w=800',
      whatYouWillLearn: [
        'Master advanced upper-intermediate Mandarin Chinese',
        'Learn 600+ additional HSK3 Part 2 vocabulary words',
        'Master complex narrative and storytelling techniques',
        'Develop advanced writing and composition skills',
        'Understand nuanced conversational expressions',
        'Complete HSK3 certification preparation',
      ],
      prerequisites: ['Completion of Chinese for HSK 3 Part 1 or equivalent knowledge', 'Strong intermediate Mandarin skills'],
      schedule: [
        { week: 'Week 1', topic: 'Advanced Narrative & Storytelling' },
        { week: 'Week 2', topic: 'Complex Vocabulary & Expressions' },
        { week: 'Week 3', topic: 'Writing & Composition Skills' },
        { week: 'Week 4', topic: 'Final HSK3 Exam Preparation' },
      ],
      courseStructure: [
        { type: 'Video Lectures', description: 'High-quality instructional videos from Peking University', icon: 'Video' },
        { type: 'Practice Exercises', description: 'Interactive drills and vocabulary practice', icon: 'CheckCircle' },
        { type: 'Quizzes & Exams', description: 'Graded assessments and practice HSK3 exams', icon: 'Award' },
        { type: 'Discussion Forums', description: 'Peer support and community learning', icon: 'Users' },
        { type: 'Resource Materials', description: 'Downloadable study guides and vocabulary lists', icon: 'Book' },
        { type: 'Certificate', description: 'Verified Coursera certificate of completion', icon: 'Award' },
      ],
      certificationInfo: 'Upon completion, earn a Coursera Certificate of Completion recognized by employers and educational institutions worldwide. This course completes HSK3 certification preparation.',
      accessDuration: '6 months access (with option to extend)',
      refundPolicy: '7-day free trial, full refund available',
      supportAvailable: true,
      supportChannels: ['Discussion Forums', 'Community Support', 'Course Messages'],
    },
    {
      id: 11,
      title: 'Chinese for HSK 4',
      description: 'Official Coursera course by Peking University. Reach upper-intermediate proficiency with advanced vocabulary, complex writing, and professional communication.',
      category: 'mooc',
      icon: 'Award',
      level: 'Upper-Intermediate',
      instructor: 'Peking University',
      instructorBio: 'Peking University is one of China\'s most prestigious universities, renowned for its Chinese language education programs. Their faculty are experts in teaching Mandarin Chinese to international learners with engaging and comprehensive curricula.',
      instructorExperience: 'Peking University Faculty',
      instructorStudents: 10000,
      instructorRating: 5,
      duration: '5 weeks • 20 hours',
      durationWeeks: 5,
      hoursPerWeek: 4,
      students: 10000,
      rating: 5,
      reviews: 1543,
      price: 0,
      courseraUrl: 'https://www.coursera.org/learn/hsk-4',
      demoVideoUrl: 'https://www.coursera.org/learn/hsk-4',
      imageUrl: 'https://images.unsplash.com/photo-1513338261602-ecdc8035d8cf?w=800',
      whatYouWillLearn: [
        'Achieve upper-intermediate Mandarin Chinese proficiency',
        'Learn 1,200+ essential HSK4 vocabulary words',
        'Master complex grammar structures and writing techniques',
        'Develop professional and business communication skills',
        'Understand advanced Chinese media and literature',
        'Prepare for official HSK4 certification exam',
      ],
      prerequisites: ['Completion of Chinese for HSK 3 Part 2 or equivalent knowledge', 'Advanced intermediate Mandarin skills'],
      schedule: [
        { week: 'Week 1-2', topic: 'Advanced Grammar & Complex Structures' },
        { week: 'Week 2-3', topic: 'Professional & Business Communication' },
        { week: 'Week 3-4', topic: 'Advanced Writing & Composition' },
        { week: 'Week 4-5', topic: 'HSK4 Exam Preparation & Mastery' },
      ],
      courseStructure: [
        { type: 'Video Lectures', description: 'High-quality instructional videos from Peking University', icon: 'Video' },
        { type: 'Practice Exercises', description: 'Interactive drills and advanced vocabulary practice', icon: 'CheckCircle' },
        { type: 'Quizzes & Exams', description: 'Graded assessments and full-length HSK4 practice exams', icon: 'Award' },
        { type: 'Discussion Forums', description: 'Peer support and advanced learner community', icon: 'Users' },
        { type: 'Resource Materials', description: 'Comprehensive study guides, texts, and materials', icon: 'Book' },
        { type: 'Certificate', description: 'Verified Coursera certificate of completion', icon: 'Award' },
      ],
      certificationInfo: 'Upon completion, earn a Coursera Certificate of Completion recognized by employers and educational institutions worldwide. This course prepares you for the official HSK4 certification exam.',
      accessDuration: '6 months access (with option to extend)',
      refundPolicy: '7-day free trial, full refund available',
      supportAvailable: true,
      supportChannels: ['Discussion Forums', 'Community Support', 'Course Messages'],
    },
    {
      id: 12,
      title: 'Chinese for HSK 5',
      description: 'Official Coursera course by Peking University. Achieve advanced proficiency with mastery of complex topics, professional settings, and cultural nuance.',
      category: 'mooc',
      icon: 'Award',
      level: 'Advanced',
      instructor: 'Peking University',
      instructorBio: 'Peking University is one of China\'s most prestigious universities, renowned for its Chinese language education programs. Their faculty are experts in teaching Mandarin Chinese to international learners with engaging and comprehensive curricula.',
      instructorExperience: 'Peking University Faculty',
      instructorStudents: 7500,
      instructorRating: 5,
      duration: '5 weeks • 22 hours',
      durationWeeks: 5,
      hoursPerWeek: 4.4,
      students: 7500,
      rating: 5,
      reviews: 1089,
      price: 0,
      courseraUrl: 'https://www.coursera.org/learn/chinese-for-hsk5',
      demoVideoUrl: 'https://www.coursera.org/learn/chinese-for-hsk5',
      imageUrl: 'https://images.unsplash.com/photo-1513338261602-ecdc8035d8cf?w=800',
      whatYouWillLearn: [
        'Achieve advanced Mandarin Chinese proficiency',
        'Learn 2,500+ advanced HSK5 vocabulary words',
        'Master sophisticated grammar and complex writing',
        'Understand nuanced business and academic discourse',
        'Comprehend authentic Chinese media and literature',
        'Prepare for official HSK5 certification exam',
      ],
      prerequisites: ['Completion of Chinese for HSK 4 or equivalent knowledge', 'Upper-intermediate to advanced Mandarin skills'],
      schedule: [
        { week: 'Week 1-2', topic: 'Advanced Topics & Complex Discourse' },
        { week: 'Week 2-3', topic: 'Academic & Professional Communication' },
        { week: 'Week 3-4', topic: 'Advanced Writing & Cultural Analysis' },
        { week: 'Week 4-5', topic: 'HSK5 Mastery & Final Examination Prep' },
      ],
      courseStructure: [
        { type: 'Video Lectures', description: 'High-quality instructional videos from Peking University', icon: 'Video' },
        { type: 'Practice Exercises', description: 'Advanced drills and sophisticated vocabulary practice', icon: 'CheckCircle' },
        { type: 'Quizzes & Exams', description: 'Graded assessments and comprehensive HSK5 practice exams', icon: 'Award' },
        { type: 'Discussion Forums', description: 'Advanced learner community and peer support', icon: 'Users' },
        { type: 'Resource Materials', description: 'Academic texts, professional materials, and cultural resources', icon: 'Book' },
        { type: 'Certificate', description: 'Verified Coursera certificate of completion', icon: 'Award' },
      ],
      certificationInfo: 'Upon completion, earn a Coursera Certificate of Completion recognized by employers and educational institutions worldwide. This course prepares you for the official HSK5 certification exam.',
      accessDuration: '6 months access (with option to extend)',
      refundPolicy: '7-day free trial, full refund available',
      supportAvailable: true,
      supportChannels: ['Discussion Forums', 'Community Support', 'Course Messages'],
    },
    {
      id: 13,
      title: 'Chinese for HSK 6',
      description: 'Official Coursera course by Peking University. Achieve mastery level proficiency with complete fluency in all contexts and native-level comprehension.',
      category: 'mooc',
      icon: 'Award',
      level: 'Mastery',
      instructor: 'Peking University',
      instructorBio: 'Peking University is one of China\'s most prestigious universities, renowned for its Chinese language education programs. Their faculty are experts in teaching Mandarin Chinese to international learners with engaging and comprehensive curricula.',
      instructorExperience: 'Peking University Faculty',
      instructorStudents: 5000,
      instructorRating: 5,
      duration: '6 weeks • 25 hours',
      durationWeeks: 6,
      hoursPerWeek: 4.17,
      students: 5000,
      rating: 5,
      reviews: 723,
      price: 0,
      courseraUrl: 'https://www.coursera.org/learn/chinese-for-hsk-6',
      demoVideoUrl: 'https://www.coursera.org/learn/chinese-for-hsk-6',
      imageUrl: 'https://images.unsplash.com/photo-1513338261602-ecdc8035d8cf?w=800',
      whatYouWillLearn: [
        'Achieve mastery-level Mandarin Chinese proficiency',
        'Learn 5,000+ comprehensive HSK6 vocabulary',
        'Master sophisticated linguistic and stylistic variation',
        'Understand specialized academic and professional discourse',
        'Comprehend native-level media, literature, and discourse',
        'Achieve fluency in all social, professional, and academic contexts',
      ],
      prerequisites: ['Completion of Chinese for HSK 5 or equivalent knowledge', 'Advanced Mandarin proficiency'],
      schedule: [
        { week: 'Week 1-2', topic: 'Specialized Vocabulary & Advanced Discourse' },
        { week: 'Week 3-4', topic: 'Professional Mastery & Stylistic Variation' },
        { week: 'Week 4-5', topic: 'Literary Analysis & Cultural Sophistication' },
        { week: 'Week 5-6', topic: 'Fluency Mastery & HSK6 Final Examination' },
      ],
      courseStructure: [
        { type: 'Video Lectures', description: 'Advanced instructional content from Peking University', icon: 'Video' },
        { type: 'Practice Exercises', description: 'Mastery-level drills and authentic material practice', icon: 'CheckCircle' },
        { type: 'Quizzes & Exams', description: 'Comprehensive assessments and full HSK6 practice exams', icon: 'Award' },
        { type: 'Discussion Forums', description: 'Mastery-level learner community and advanced discussions', icon: 'Users' },
        { type: 'Resource Materials', description: 'Advanced academic, professional, and literary materials', icon: 'Book' },
        { type: 'Certificate', description: 'Verified Coursera certificate of completion', icon: 'Award' },
      ],
      certificationInfo: 'Upon completion, earn a Coursera Certificate of Completion recognized by employers and educational institutions worldwide. This course prepares you for the official HSK6 certification exam, the highest level of HSK proficiency.',
      accessDuration: '6 months access (with option to extend)',
      refundPolicy: '7-day free trial, full refund available',
      supportAvailable: true,
      supportChannels: ['Discussion Forums', 'Community Support', 'Course Messages'],
    },
  ];

  const course = useMemo(() => courses.find((c) => c.id === parseInt(courseId)), [courseId]);

  if (!course) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-32 pb-16">
          <div className="max-w-[1200px] mx-auto px-6 text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Course Not Found</h1>
            <Button
              variant="default"
              onClick={() => navigate('/online-learning')}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Back to Courses
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const findInstructor = (instructorName) => {
    const teachers = [
      {
        name: 'Li Wei',
        specialization: 'Beginner & Fundamentals',
        bio: 'Certified TCFL instructor with 8+ years of experience teaching international students.',
        experience: '8+ years teaching experience',
        studentsCount: 2500,
        rating: 5,
      },
    ];
    return teachers.find((t) => t.name === instructorName);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section with Image */}
      <section className="pt-24 pb-12 bg-gradient-to-r from-primary/10 to-primary/5">
        <div className="max-w-[1200px] mx-auto px-6">
          <button
            onClick={() => navigate('/online-learning')}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-smooth mb-6 font-medium"
          >
            <Icon name="ArrowLeft" size={18} />
            Back to Courses
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <span className="inline-block bg-primary/10 text-primary px-4 py-1 rounded-full text-sm font-semibold mb-4">
                {course.level}
              </span>
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
                {course.title}
              </h1>
              <p className="text-lg text-muted-foreground mb-6">{course.description}</p>

              <div className="flex flex-wrap gap-6 mb-6">
                <div className="flex items-center gap-3">
                  <Icon name="Star" size={20} color="var(--color-primary)" />
                  <div>
                    <div className="font-semibold text-foreground">{course.rating}/5</div>
                    <div className="text-sm text-muted-foreground">({course.reviews} reviews)</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="Users" size={20} color="var(--color-primary)" />
                  <div>
                    <div className="font-semibold text-foreground">{course.students}</div>
                    <div className="text-sm text-muted-foreground">students enrolled</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="Clock" size={20} color="var(--color-primary)" />
                  <div>
                    <div className="font-semibold text-foreground">{course.duration}</div>
                    <div className="text-sm text-muted-foreground">{course.durationWeeks} weeks</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-warm p-6">
              <div className="mb-6">
                <div className="text-sm text-muted-foreground mb-2">Price</div>
                <div className="text-4xl font-bold text-primary mb-4">
                  {course.price === 0 ? 'FREE' : `$${course.price}`}
                </div>
              </div>

              <Button
                variant="default"
                fullWidth
                iconName={course.category === 'mooc' ? 'ExternalLink' : 'ShoppingCart'}
                iconPosition="left"
                className="mb-3"
                onClick={() => {
                  if (course.category === 'mooc' && course.courseraUrl) {
                    window.open(course.courseraUrl, '_blank');
                  } else {
                    alert(`Enrollment for ${course.title} will be processed`);
                  }
                }}
              >
                {course.category === 'mooc' ? 'Enroll on Coursera' : 'Enroll Now'}
              </Button>

              <div className="space-y-4 mb-6 pt-6 border-t border-border">
                <div className="flex items-center gap-3">
                  <Icon name="CheckCircle" size={20} color="var(--color-primary)" />
                  <span className="text-sm text-foreground">{course.accessDuration}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="RotateCcw" size={20} color="var(--color-primary)" />
                  <span className="text-sm text-foreground">{course.refundPolicy}</span>
                </div>
                {course.supportAvailable && (
                  <div className="flex items-center gap-3">
                    <Icon name="MessageCircle" size={20} color="var(--color-primary)" />
                    <span className="text-sm text-foreground">24/7 Support</span>
                  </div>
                )}
              </div>

              <div className="text-xs text-muted-foreground bg-muted p-3 rounded">
                Includes all course materials, lifetime access, and certification
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Video Section */}
      <section className="py-12 bg-muted/30 border-t border-b border-border">
        <div className="max-w-[1200px] mx-auto px-6">
          <h2 className="text-2xl font-heading font-bold text-foreground mb-6">Preview the Course</h2>
          <div className="bg-black rounded-lg overflow-hidden shadow-lg">
            {course.category === 'mooc' && course.demoVideoUrl ? (
              <div className="relative w-full h-96 bg-gradient-to-b from-slate-800 to-slate-900 flex flex-col items-center justify-center gap-6 p-8">
                <Icon name="Video" size={80} color="var(--color-primary)" />
                <div className="text-center">
                  <h3 className="text-white text-2xl font-bold mb-2">Watch on Coursera</h3>
                  <p className="text-slate-300 mb-6">This course is hosted on Coursera. Click below to view the full course and preview content.</p>
                  <Button
                    variant="default"
                    iconName="ExternalLink"
                    iconPosition="right"
                    onClick={() => window.open(course.demoVideoUrl, '_blank')}
                  >
                    View Course Preview on Coursera
                  </Button>
                </div>
              </div>
            ) : (
              <iframe
                width="100%"
                height="500"
                src={course.demoVideoUrl}
                title={`${course.title} Demo`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            )}
          </div>
        </div>
      </section>

      {/* Tabs Navigation */}
      <section className="py-12 bg-background">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex flex-wrap gap-2 border-b border-border mb-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'curriculum', label: 'Curriculum' },
              { id: 'schedule', label: 'Schedule' },
              { id: 'instructor', label: 'Instructor' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 font-medium text-sm transition-smooth border-b-2 ${
                  activeTab === tab.id
                    ? 'text-primary border-primary'
                    : 'text-muted-foreground border-transparent hover:text-foreground'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-foreground mb-4">What You'll Learn</h3>
                  <ul className="space-y-3">
                    {course.whatYouWillLearn.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Icon name="CheckCircle" size={20} color="var(--color-primary)" className="mt-1 flex-shrink-0" />
                        <span className="text-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-foreground mb-4">Course Structure</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {course.courseStructure.map((item, index) => (
                      <div key={index} className="bg-muted/50 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <Icon name={item.icon} size={20} color="var(--color-primary)" />
                          <h4 className="font-semibold text-foreground">{item.type}</h4>
                        </div>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-foreground mb-4">Prerequisites</h3>
                  <ul className="space-y-2">
                    {course.prerequisites.map((item, index) => (
                      <li key={index} className="flex items-center gap-2 text-foreground">
                        <Icon name="Circle" size={6} color="var(--color-primary)" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="bg-muted/30 rounded-lg p-6 h-fit sticky top-24">
                <h4 className="font-bold text-foreground mb-4">Quick Facts</h4>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Duration</div>
                    <div className="font-semibold text-foreground">{course.duration}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Time Commitment</div>
                    <div className="font-semibold text-foreground">{course.hoursPerWeek.toFixed(1)} hours/week</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Level</div>
                    <div className="font-semibold text-foreground">{course.level}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Students</div>
                    <div className="font-semibold text-foreground">{course.students.toLocaleString()}+</div>
                  </div>
                  <div className="pt-4 border-t border-border">
                    <div className="text-sm text-muted-foreground mb-2">Access</div>
                    <div className="font-semibold text-foreground text-sm">{course.accessDuration}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Curriculum Tab */}
          {activeTab === 'curriculum' && (
            <div className="space-y-6">
              <div className="bg-primary/5 rounded-lg p-6 mb-6">
                <p className="text-foreground">{course.certificationInfo}</p>
              </div>
              {course.schedule.map((item, index) => (
                <div key={index} className="bg-card rounded-lg p-6 border border-border hover:border-primary transition-smooth">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-bold text-foreground text-lg">{item.week}</h4>
                    <Icon name="BookOpen" size={24} color="var(--color-primary)" />
                  </div>
                  <p className="text-muted-foreground">{item.topic}</p>
                </div>
              ))}
            </div>
          )}

          {/* Schedule Tab */}
          {activeTab === 'schedule' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-card rounded-lg p-6 border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="Calendar" size={24} color="var(--color-primary)" />
                    <h4 className="font-bold text-foreground">Course Duration</h4>
                  </div>
                  <p className="text-2xl font-bold text-primary mb-2">{course.durationWeeks} weeks</p>
                  <p className="text-muted-foreground">Flexible start dates available</p>
                </div>

                <div className="bg-card rounded-lg p-6 border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="Clock" size={24} color="var(--color-primary)" />
                    <h4 className="font-bold text-foreground">Weekly Commitment</h4>
                  </div>
                  <p className="text-2xl font-bold text-primary mb-2">{course.hoursPerWeek.toFixed(1)} hours</p>
                  <p className="text-muted-foreground">Flexible scheduling available</p>
                </div>
              </div>

              <div className="bg-card rounded-lg p-6 border border-border">
                <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Video" size={20} color="var(--color-primary)" />
                  Class Schedule
                </h4>
                <div className="space-y-2 text-foreground">
                  <p>• Live classes: 2-3 times per week</p>
                  <p>• Class duration: 60-90 minutes each</p>
                  <p>• Time zones: Sessions scheduled for international students</p>
                  <p>• Recordings: All classes recorded for on-demand access</p>
                </div>
              </div>
            </div>
          )}

          {/* Instructor Tab */}
          {activeTab === 'instructor' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="flex items-center gap-6 mb-8">
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0">
                    <Icon name="User" size={64} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-foreground mb-2">{course.instructor}</h3>
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(course.instructorRating)].map((_, i) => (
                        <Icon key={i} name="Star" size={18} color="var(--color-primary)" />
                      ))}
                      <span className="ml-2 text-foreground font-semibold">{course.instructorRating}/5</span>
                    </div>
                    <p className="text-muted-foreground">{course.instructorExperience}</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-bold text-foreground mb-3">About the Instructor</h4>
                  <p className="text-foreground leading-relaxed mb-6">{course.instructorBio}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <Icon name="BookOpen" size={24} color="var(--color-primary)" className="mb-2" />
                    <div className="text-2xl font-bold text-foreground">{course.instructorExperience}</div>
                    <div className="text-sm text-muted-foreground">Teaching Experience</div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <Icon name="Users" size={24} color="var(--color-primary)" className="mb-2" />
                    <div className="text-2xl font-bold text-foreground">{course.instructorStudents}+</div>
                    <div className="text-sm text-muted-foreground">Students Taught</div>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg p-6 border border-border h-fit sticky top-24">
                <h4 className="font-bold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="MessageCircle" size={20} color="var(--color-primary)" />
                  Contact Instructor
                </h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Have questions? Get in touch with {course.instructor}
                </p>
                <Button
                  variant="outline"
                  fullWidth
                  iconName="Mail"
                  iconPosition="left"
                  className="mb-3"
                >
                  Send Message
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  iconName="Video"
                  iconPosition="left"
                  onClick={() => setIsScheduleModalOpen(true)}
                >
                  Book 1-on-1
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Support Section */}
      <section className="py-12 bg-muted/30 border-t border-border">
        <div className="max-w-[1200px] mx-auto px-6">
          <h3 className="text-2xl font-bold text-foreground mb-8">Support & Resources</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card rounded-lg p-6">
              <Icon name="MessageCircle" size={32} color="var(--color-primary)" className="mb-3" />
              <h4 className="font-bold text-foreground mb-2">24/7 Support</h4>
              <p className="text-muted-foreground text-sm">Get help when you need it through email, chat, and community forums</p>
            </div>
            <div className="bg-card rounded-lg p-6">
              <Icon name="Book" size={32} color="var(--color-primary)" className="mb-3" />
              <h4 className="font-bold text-foreground mb-2">Study Materials</h4>
              <p className="text-muted-foreground text-sm">Comprehensive guides, flashcards, and downloadable resources</p>
            </div>
            <div className="bg-card rounded-lg p-6">
              <Icon name="Award" size={32} color="var(--color-primary)" className="mb-3" />
              <h4 className="font-bold text-foreground mb-2">Certification</h4>
              <p className="text-muted-foreground text-sm">Earn recognized credentials upon course completion</p>
            </div>
          </div>
        </div>
      </section>

      {/* Enrollment CTA */}
      <section className="py-12 bg-primary/5 border-t border-border">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">Ready to Start Learning?</h2>
          <p className="text-lg text-muted-foreground mb-8">Enroll now and begin your journey to Chinese language fluency</p>
          <Button
            variant="default"
            iconName={course.category === 'mooc' ? 'ExternalLink' : 'ShoppingCart'}
            iconPosition="left"
            onClick={() => {
              if (course.category === 'mooc' && course.courseraUrl) {
                window.open(course.courseraUrl, '_blank');
              } else {
                alert('Enrollment process will be implemented');
              }
            }}
          >
            {course.category === 'mooc' ? `Enroll on Coursera - FREE` : `Enroll Now - $${course.price}`}
          </Button>
        </div>
      </section>

      {/* Schedule Consultation Modal */}
      <ScheduleConsultationModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        instructorName={course?.instructor || 'Instructor'}
      />

      <Footer />
    </div>
  );
};

export default CourseDetail;
